import type {
  ChannexPublicationResponse,
  PropertyCommunicationCatalogResponse,
  PropertyGroupResponse,
  PropertyPortalBrandingResponse,
} from '@/shared/api';
import type { ObjectAreaId } from './objectAreas';

/**
 * `ok` wird als Häkchen gezeigt, `open` als „offen", `attention` verlangt einen Blick, `neutral`
 * ist reine Auskunft. Jeder Hinweis trägt ein Wort; Farbe allein erklärt nichts.
 */
export type AreaHintTone = 'ok' | 'open' | 'attention' | 'neutral';

export interface AreaHint {
  tone: AreaHintTone;
  /** Sichtbarer Kurztext; beim Häkchen entfällt er. */
  text?: string;
  /** Vollständige Aussage für Screenreader und Tooltips. */
  label: string;
}

/** Nur was geladen wurde, fließt ein — fehlende Daten erzeugen keinen Hinweis (Brief-Tabu). */
export interface AreaStatusInput {
  group: PropertyGroupResponse;
  arrivalSteps?: number;
  parkingSpots?: number;
  parkingAlternatives?: number;
  catalog?: PropertyCommunicationCatalogResponse;
  branding?: PropertyPortalBrandingResponse;
  publication?: ChannexPublicationResponse;
  /** Bereiche, deren Stand nicht geladen werden konnte — keine Aussage, aber auch kein Schweigen. */
  unavailable?: ObjectAreaId[];
}

export function objectAreaHints(input: AreaStatusInput): Partial<Record<ObjectAreaId, AreaHint>> {
  const { group } = input;
  const hints: Partial<Record<ObjectAreaId, AreaHint>> = {
    stammdaten: group.needsAddressReview
      ? group.positionLookupPending
        ? { tone: 'open', text: 'wird geprüft', label: 'Position wird gesucht' }
        : { tone: 'attention', text: 'prüfen', label: 'Adresse bitte prüfen' }
      : typeof group.address.latitude !== 'number'
        ? { tone: 'open', text: 'ohne Pin', label: 'noch keine Position auf der Karte' }
        : { tone: 'ok', label: 'vollständig' },
    hausordnung:
      group.houseRulesDe?.trim() || group.houseRulesEn?.trim()
        ? { tone: 'ok', label: 'hinterlegt' }
        : { tone: 'open', text: 'offen', label: 'noch nicht hinterlegt' },
  };
  if (input.arrivalSteps !== undefined) {
    hints.anfahrt =
      input.arrivalSteps > 0
        ? neutral(count(input.arrivalSteps, 'Schritt', 'Schritte'))
        : { tone: 'open', text: 'offen', label: 'noch keine Schritte' };
  }
  if (input.parkingSpots !== undefined && input.parkingSpots > 0) {
    hints.parken = neutral(count(input.parkingSpots, 'Platz', 'Plätze'));
  }
  if (input.catalog) {
    const active = input.catalog.messages.filter((message) => message.isEnabled).length;
    hints.nachrichten = neutral(`${active}/${input.catalog.messages.length} aktiv`);
  }
  if (input.branding) {
    const own = !!input.branding.brandName?.trim() || input.branding.hasLogo;
    hints.gastportal = neutral(own ? 'Eigene Marke' : 'StayOn');
  }
  if (input.publication && input.publication.units.length > 0) {
    const published = input.publication.units.filter((unit) => unit.state === 'Published').length;
    hints.veroeffentlichung = {
      tone: 'neutral',
      text: `${published}/${input.publication.units.length}`,
      label: `${published} von ${input.publication.units.length} Apartments veröffentlicht`,
    };
  }
  return hints;
}

function count(value: number, one: string, many: string) {
  return `${value} ${value === 1 ? one : many}`;
}

function neutral(text: string): AreaHint {
  return { tone: 'neutral', text, label: text };
}
